import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { ButtonPrimary } from "../../../components/utils";
import { employeeUploadTemplate } from "../../../api/employeeImport";
import convertToBase64 from "../../../utils/convertToBase64";
import {
  useParseEmployeeMetaData,
  useUpdateEmployeeMetaDataBulk,
  useVerifyEmployeeMetaDataUploadTemplate,
} from "../../../queries/employees";
import { EmployeeMetaDataRow } from "../../../types/Employee";
import EmployeeMetaDataPreview from "./EmployeeMetaDataPreview";
import { FileDropzone } from "../../../components/shared/FileDropzone";
import { Instructions } from "../../../components/shared/Instructions";

const EmployeeMetaData = () => {
  const [file, setFile] = useState<File>();
  const [errors, setErrors] = useState<string[]>([]);
  const { mutate: verifyTemplateData, isLoading: verifyingData } =
    useVerifyEmployeeMetaDataUploadTemplate();

  const { mutate: parseMetaData, isLoading: parsingData } =
    useParseEmployeeMetaData();

  const navigate = useNavigate();

  const { mutate: updateMetaData } = useUpdateEmployeeMetaDataBulk();

  const [previewData, setPreviewData] = useState<EmployeeMetaDataRow[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const handleDownloadTemplate = async () => {
    try {
      const response = await employeeUploadTemplate();
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "employee_upload_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      toast.error("Error downloading template");
    }
  };

  const handlePreview = async () => {
    if (!file) return;
    const base64File = (await convertToBase64(file)) as string;
    verifyTemplateData(base64File, {
      onSuccess: (data) => {
        if (data && data.errors && data.errors.length > 0) {
          setErrors(data.errors);
        } else {
          parseMetaData(base64File, {
            onSuccess: (data) => {
              toast.success("Employee Meta Data validated successfully");
              setPreviewData(data);
              setShowPreview(true);
            },
            onError: (error: any) => {
              toast.error(
                error?.response?.data?.message ||
                  "Error parsing Employee Meta Data"
              );
            },
          });
        }
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message ||
            "Error validating Employee Meta Data"
        );
      },
    });
  };

  async function handleFileDrop(newFile: File) {
    setFile(newFile);
    setErrors([]);
  }

  if (showPreview) {
    return (
      <EmployeeMetaDataPreview
        data={previewData}
        onCancel={() => {
          setShowPreview(false);
          setPreviewData([]);
          setFile(undefined);
          setErrorMessage("");
        }}
        errorMessage={errorMessage}
        onUpdate={(selectedRows) => {
          updateMetaData(selectedRows, {
            onSuccess: () => {
              setShowPreview(false);
              setPreviewData([]);
              setFile(undefined);
              toast.success("Employee details updated successfully");
              navigate("/options/allEmployees");
            },
            onError: (err: any) => {
              setErrorMessage(
                err?.response?.data?.errorRaw ||
                  "Error updating Employee Meta Data"
              );
              toast.error("Error updating Employee Meta Data");
            },
          });
        }}
      />
    );
  }

  return (
    <div className="w-full h-full bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col min-h-[600px]">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Update Employee Details
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Upload an Excel file to bulk update employee information
          </p>
        </div>
        <ButtonPrimary
          className="flex items-center gap-2"
          onClick={handleDownloadTemplate}
        >
          <Icon icon="mdi:download-outline" height={18} />
          Download Template
        </ButtonPrimary>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-2xl space-y-8">
          <div className="text-center space-y-2">
            <h3 className="text-xl font-medium text-gray-900">Upload File</h3>
            <p className="text-gray-500">
              Please ensure your file follows the template format before
              uploading.
            </p>
          </div>

          <div className="w-full max-w-2xl mx-auto">
            <FileDropzone
              onFileSelect={handleFileDrop}
              selectedFile={file}
              onClear={() => {
                setFile(undefined);
                setErrors([]);
              }}
            />

            {errors.length > 0 && (
              <div className="mt-6 p-4 border border-red-200 bg-red-50 rounded-md">
                <div className="flex items-center gap-2 mb-2 text-red-700">
                  <Icon icon="material-symbols:error-outline" height={20} />
                  <span className="font-semibold">
                    {errors.length} Errors Found
                  </span>
                </div>
                <ul className="list-disc list-inside space-y-1 text-sm text-red-600 max-h-40 overflow-y-auto pl-2">
                  {errors.map((err, index) => (
                    <li key={index}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <Instructions
            data={[
              "Use the downloaded template to ensure correct format",
              "Update details like Designation, Department, Address, Tax details, etc.",
              "For Nominees, you can specify up to 2 nominees with Name, Relationship, Percentage, DOB, Address, Contact, and Email",
              "Guardian details (Name, Relationship, Contact, Email, DOB, Address) are required when nominee is a minor",
              "Ensure total nominee percentage equals 100% if nominees are provided",
              "Date of Joining and other dates should be in YYYY-MM-DD or valid Excel date format",
              "Tax Regime can be updated using 'TRUE' (New Regime) or 'FALSE' (Old Regime)",
              "You can provide up to two demat account IDs. Each ID must be 16 alphanumeric characters.",
              "Providing either demat account ID replaces the employee's saved demat accounts and removes linked CML documents. Leave both demat columns blank to keep existing accounts.",
            ]}
          />

          {file && (
            <div className="flex justify-center pt-4">
              <ButtonPrimary
                className="h-10 text-base shadow-md hover:shadow-lg transition-shadow"
                onClick={handlePreview}
                disabled={parsingData}
              >
                {parsingData ? (
                  <div className="flex items-center gap-2">
                    <Icon icon="eos-icons:loading" className="animate-spin" />
                    <span>Processing...</span>
                  </div>
                ) : (
                  "Preview Changes"
                )}
              </ButtonPrimary>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeMetaData;
